import {Option} from 'platform/components';

import {isNil, join, pipe, reject} from 'ramda';
import {isEmptyString, isNilOrEmpty, isNonEmptyString} from 'ramda-adjunct';

import {GetApiMakeModelWithMakeApiResponse} from '@dms/api/shared';
import {i18n} from '@dms/i18n';

import {Nullish} from 'shared/web';

type Vehicle = {
  make?: string | null;
  modelFamily?: string | null;
  trim?: string | null;
  variant?: string | null;
  title?: string | null;
};

export interface VehicleLabels {
  make: string | undefined;
  modelFamily: string | undefined;
  trim: string | undefined;
  variant: string | undefined;
}

/**
 * Custom tenant catalogue options, as returned by `useCustomTenantCatalogue`.
 * A custom entry is keyed by its own label, so `value` holds the text the user typed
 * rather than a `MAKE_*` catalogue code.
 */
export type CustomVehicleCatalogueOptions = {
  makeOptions: Option[] | Nullish;
  modelOptions: Option[] | Nullish;
};

/**
 * Resolves the display labels for a vehicle from the make-model catalogue, falling
 * back to the custom tenant catalogue.
 *
 * Returns the localized (or default_label fallback) label for make and modelFamily.
 * `trim` and `variant` are passed through from the vehicle as-is since they are
 * not part of the make-model catalogue.
 *
 * Returns `undefined` for a given field if it cannot be resolved.
 */
export function getVehicleLabels(
  vehicle: Vehicle,
  makeModels?: GetApiMakeModelWithMakeApiResponse | Nullish,
  customCatalogue?: CustomVehicleCatalogueOptions | Nullish
): VehicleLabels {
  const language = i18n?.resolvedLanguage ?? 'en';
  const makeModel = makeModels?.find((entry) => entry.make === vehicle.make);

  const standardMake = isNil(makeModel) ? undefined : getLabel(makeModel, language);
  const standardModelFamily = isNil(makeModel)
    ? undefined
    : getLabel(
        makeModel.models?.find((model) => model.model === vehicle.modelFamily),
        language
      );

  return {
    make: standardMake ?? getOptionLabel(customCatalogue?.makeOptions, vehicle.make),
    modelFamily:
      standardModelFamily ?? getOptionLabel(customCatalogue?.modelOptions, vehicle.modelFamily),
    trim: vehicle.trim ?? undefined,
    variant: vehicle.variant ?? undefined,
  };
}

export function getVehicleTitle(
  vehicle: Vehicle,
  makeModels?: GetApiMakeModelWithMakeApiResponse | Nullish,
  customCatalogue?: CustomVehicleCatalogueOptions | Nullish
) {
  if (isNonEmptyString(vehicle.title)) {
    return vehicle.title;
  }

  const labels = getVehicleLabels(vehicle, makeModels, customCatalogue);

  if (isNilOrEmpty(labels.make)) {
    return null;
  }

  return pipe(
    reject(isNilOrEmpty),
    join(' ')
  )([labels.make, labels.modelFamily, labels.trim, labels.variant]);
}

type LabelType = {
  default_label: string;
  labels?: {
    label: string;
    label_short?: string | null;
    language: string;
  }[];
};

const getLabel = (vehicleModel: LabelType | Nullish, language: string) =>
  vehicleModel?.labels?.find((label) => label.language.toUpperCase() === language.toUpperCase())
    ?.label ?? vehicleModel?.default_label;

const getOptionLabel = (options: Option[] | Nullish, value: string | Nullish) =>
  isNil(value) || isEmptyString(value)
    ? undefined
    : options?.find((option) => option.value === value)?.label;
